import React from "react"
import {
  Flex,
  Box,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  useDisclosure,
  DrawerCloseButton,
  useToast,
  Text
} from "@chakra-ui/core"
import * as R from "ramda"
import {
  ArrayParam,
  BooleanParam,
  StringParam,
  useQueryParam,
} from "use-query-params"
import { FluidObject } from "gatsby-image"
import { useSelector, useDispatch } from "react-redux"
import { Link as GatsbyLink, navigate, PageProps } from "gatsby"

import BikeListLayout, {
  BikeListLayoutProps,
} from "../../components/Layout/BikeListLayout"
import MaxWidthGrid from "../../components/Layout/MaxWidthGrid"
import Heading from "../../components/typography/Heading"
import Body from "../../components/typography/Body"
import SEOBlock from "../../components/SEOBlock"
import ContentfulRichText from "../../components/ContentfulRichText"
import { BikeVariant } from "../../components/BikeProductCard"
import { FitType } from "../../components/BikeProductPage/QuickFit"
import CompareBar from "../../components/CompareBar"
import CompareTable from "../../components/CompareTable"
import SEO from "../../components/boilerplate/seo"

import MobileFilters from "./MobileFilters"
import DesktopFilters from "./DesktopFilters"
import InStockFilter from "./InStockFilter"
import SelectedFilters from "./SelectedFilters"
import SortDropdown from "./SortDropdown"

import {
  OptionType,
  Filter,
  isAtLeastOneFilterAppliedByTitle,
  shouldNotFilterBike,
} from "./utils"
import { placeholderImage } from "../../utils"
import { BikeListingType } from "../../utils/withTransformData"
import { LaptopUpOnly, LaptopDownOnly, bp } from "../../utils/MediaQueries"
import { sortMethods } from "../../utils/sort"
import { isFit } from "../../utils/bodyFit"
import { BaseRootState, BaseDispatch } from "../../redux/store"
import useBreakpointValue from "../../hooks/useBreakpointValue"
import useLayoutMeasurements from "../../hooks/useLayoutMeasurements"
import useShopifyVariantPricing from "../../hooks/useShopifyVariantPricing"
import { getFixedPrice } from "../../components/Cart/utils"
import { BicycleProductListQuery } from "../../../graphql-types"
import Banner from "../../components/Banner"

export interface BicycleProductListProps {
  data: Partial<BicycleProductListQuery> & {
    bikes: Array<BikeListingType>
  }
  pageContext: {
    pageTitle: string
    collectionType?: string
    bicycleSlugs?: string[]
  }
  location?: Location
}

export type CompareBike = {
  bike?: BicycleProductListQuery["bicycles"]["edges"][0]["node"] | null
  variant: BikeVariant
}

const CompareToast = (props) => (
  <Box
    backgroundColor="night"
    padding="0.8125rem 1.1875rem"
    color="white"
    borderRadius="0.5rem"
    marginTop="4.6rem"
    onClick={props.onClose}
  >
    Select up to three bikes to compare
  </Box>
)

const Link: React.FC<{ children: string; to: string }> = ({ children, to }) => (
  <GatsbyLink to={to}>
    <Text
      as="span"
      color="primary"
      borderBottom="1px solid"
      borderBottomColor="primary"
      paddingBottom="2px"
    >
      {children}
    </Text>
  </GatsbyLink>
)

const BicycleProductList: React.FC<BicycleProductListProps> = ({
  data,
  pageContext,
  location,
}) => {
  const { allBikes, bikeResults, answers, bodyFit, isSidebarOpen } =
    useSelector((state: BaseRootState) => ({
      allBikes: state.bikes.bikes,
      bikeResults: state.bikes.bikeResults,
      answers: state.quiz.answers,
      bodyFit: state.user.bodyFit,
      isSidebarOpen: state.sidebar.isOpen,
    }))
  const dispatch = useDispatch<BaseDispatch>()
  const isMobile = useBreakpointValue([true, null, null, null, false])
  const { contentWidthResponsive } = useLayoutMeasurements()
  const [compareEnabled, setComparedEnabled] = React.useState(false)
  const [compareBikes, setCompareBikes] = React.useState<CompareBike[]>([])
  const compareModal = useDisclosure()
  const toast = useToast()

  // Calculate bike results if quiz taken
  React.useEffect(() => {
    if (bikeResults === null && answers.date !== "") {
      dispatch.bikes.calculateResults()
    }
  }, [bikeResults, answers])

  // Pricing
  const { pricingMap } = useShopifyVariantPricing([
    ...R.uniq(
      R.flatten(
        data.bikes.map((bike) =>
          bike.variants.map((variant) => variant.uniqueId)
        )
      )
    ),
  ])

  // Sort
  const [sortParam, setSort] = useQueryParam("sort", StringParam)
  const sort = sortParam ? sortParam : "BY_RELEVANCE"

  // Filters
  const [styleQuery = [], setStyleQuery] = useQueryParam("style", ArrayParam)
  const [genderQuery = [], setGenderQuery] = useQueryParam("gender", ArrayParam)
  const [terrainQuery = [], setTerrainQuery] = useQueryParam(
    "terrain",
    ArrayParam
  )
  const [colorQuery = [], setColorQuery] = useQueryParam("color", ArrayParam)
  const [speedQuery = [], setSpeedQuery] = useQueryParam("speed", ArrayParam)
  const [wheelSizeQuery = [], setWheelSizeQuery] = useQueryParam(
    "wheelSize",
    ArrayParam
  )

  const queryParams = {
    style: { query: styleQuery, setQuery: setStyleQuery },
    gender: { query: genderQuery, setQuery: setGenderQuery },
    terrain: { query: terrainQuery, setQuery: setTerrainQuery },
    color: { query: colorQuery, setQuery: setColorQuery },
    speed: { query: speedQuery, setQuery: setSpeedQuery },
    wheelSize: { query: wheelSizeQuery, setQuery: setWheelSizeQuery },
  }
  const [inStockFilter = false, setInStockFilter] = useQueryParam(
    "stock",
    BooleanParam
  )
  const [filters, setFilters] = React.useState<Array<Filter>>([])

  React.useEffect(() => {
    const filterOptions = [
      pageContext.collectionType !== "gender" && {
        title: "gender",
        options: data.bikes
          .reduce((acc, cur) => {
            if (acc.find((val) => cur.gender === val.name) === undefined) {
              return [
                ...acc,
                {
                  title: cur.gender,
                  name: cur.gender,
                  selected: !!genderQuery?.includes(cur.gender),
                },
              ]
            }
            return acc
          }, [] as Array<OptionType>)
          .filter((gender) => !!gender?.title),
      },
      pageContext.collectionType !== "style" && {
        title: "style",
        options: data.bikes.reduce((acc, { styles }) => {
          const newStyles = (styles || []).filter(
            (sty) => acc.find(({ title }) => title === sty) === undefined
          )

          return [
            ...acc,
            ...newStyles.map((sty) => ({
              title: sty,
              name: sty,
              selected: !!styleQuery?.includes(sty),
            })),
          ]
        }, []),
      },
      pageContext.collectionType !== "terrain" && {
        title: "terrain",
        options: data.bikes.reduce((acc, { terrains }) => {
          if (terrains == null) return acc
          const newTerrains = terrains.filter(
            (ter) => acc.find(({ title }) => title === ter) === undefined
          )
          return [
            ...acc,
            ...newTerrains.map((ter) => ({
              title: ter,
              name: ter,
              selected: !!terrainQuery?.includes(ter),
            })),
          ]
        }, []),
      },
      {
        title: "color",
        options:
          data.colors?.edges
            .map((color) =>
              color?.node.internalTitle &&
              (color?.node.product_color ?? []).length > 0
                ? {
                    title: color.node.name,
                    name: color.node.internalTitle,
                    colorDot: color.node.hexColor,
                    selected: !!colorQuery?.includes(color.node.internalTitle),
                  }
                : null
            )
            .filter((x) => !!x) || [],
      },
      pageContext.collectionType !== "speed" && {
        title: "speed",
        options: data.bikes.reduce((acc, { speeds }) => {
          if (speeds == null) return acc
          const newSpeeds = speeds.filter(
            (speed) => acc.find(({ title }) => title === speed) === undefined
          )
          return [
            ...acc,
            ...newSpeeds.map((speed) => ({
              title: speed,
              name: speed,
              selected: !!speedQuery?.includes(speed),
            })),
          ]
        }, []),
      },
      pageContext.collectionType !== "wheelSize" && {
        title: "wheelSize",
        options: data.bikes.reduce((acc, { wheelSizes }) => {
          if (wheelSizes == null) return acc
          const newWheelSizes = wheelSizes.filter(
            (whsz) => acc.find(({ title }) => title === whsz) === undefined
          )
          return [
            ...acc,
            ...newWheelSizes.map((whsz) => ({
              title: whsz,
              name: whsz,
              selected: !!wheelSizeQuery?.includes(whsz),
            })),
          ]
        }, []),
      },
    ].filter((x) => !!x) as typeof filters
    setFilters(filterOptions)
  }, [])

  React.useEffect(() => {
    // Sync filter query params
    filters.forEach((filter) => {
      if (queryParams[filter.title]) {
        const { setQuery } = queryParams[filter.title]
        setQuery(
          filter.options.filter((opt) => opt.selected).map((opt) => opt.name)
        )
      }
    })
  }, [filters])

  const filteredColors = R.flatten(
    colorQuery?.map((filteredColor) => {
      return (
        data.colors?.edges
          .filter((colorFam) => colorFam.node.internalTitle === filteredColor)
          .map((colorFam) => {
            return colorFam.node.product_color?.map(
              (productColor) => productColor?.internalTitle
            )
          }) || []
      )
    }) || []
  ).filter((x) => !!x) as string[]

  const previousPath = location?.pathname
    ? location?.pathname
    : "/"

  const getBike = (internalTitle: string) =>
    //@ts-ignore
    allBikes.find((bike) => bike.internalTitle === internalTitle)
  const isCompared = (internalTitle: string) =>
    !!compareBikes.find((bike) => bike.bike?.internalTitle === internalTitle)

  const listedBikes = React.useMemo(() => {
    return (
      data.bikes
        // Gender Filter
        .filter(
          ({ gender }) =>
            !isAtLeastOneFilterAppliedByTitle("gender", filters) ||
            shouldNotFilterBike("gender", gender, filters)
        )
        // Style filter
        .filter(
          ({ styles }) =>
            !isAtLeastOneFilterAppliedByTitle("style", filters) ||
            (styles || [])
              .map((sty) => shouldNotFilterBike("style", sty, filters))
              .find((truthy) => truthy) !== undefined
        )
        // Terrain filter
        .filter(
          (bike) =>
            !isAtLeastOneFilterAppliedByTitle("terrain", filters) ||
            (bike.terrains || [])
              .map((ter) => shouldNotFilterBike("terrain", ter, filters))
              .find((truthy) => truthy) !== undefined
        )
        // Speed filter
        .filter(
          (bike) =>
            !isAtLeastOneFilterAppliedByTitle("speeds", filters) ||
            (bike.speeds || [])
              .map((spd) => shouldNotFilterBike("speeds", spd, filters))
              .find((truthy) => truthy) !== undefined
        )
        // Wheel Size filter
        .filter(
          (bike) =>
            !isAtLeastOneFilterAppliedByTitle("wheelSize", filters) ||
            (bike.wheelSizes || [])
              .map((whsz) => shouldNotFilterBike("wheelSize", whsz, filters))
              .find((truthy) => truthy) !== undefined
        )
        .map(
          ({ title, slug, internalTitle, variants, bestSeller, ...rest }) =>
            ({
              id: internalTitle,
              title,
              to: slug,
              matchPercent: bikeResults?.find(
                (result) => result.internalTitle === internalTitle
              )?.score,
              bestSeller,
              preferredIndex:
                pageContext.bicycleSlugs?.findIndex(
                  (slug) => slug === internalTitle
                ) || -1,
              fitStatus: bodyFit
                ? isFit(rest.bodyFit, bodyFit)
                  ? "fit"
                  : "no-fit"
                : (null as FitType | null),
              variants: variants
                .map(
                  (variant) =>
                    ({
                      ...variant,
                      pricing: pricingMap[variant.uniqueId],
                    } as BikeVariant)
                )
                .filter((variant) => {
                  // In-stock filter
                  if (
                    inStockFilter &&
                    (!variant.pricing?.availableForSale ||
                      variant.pricing?.currentlyNotInStock)
                  ) {
                    return false
                  }

                  // Color filter
                  if (
                    isAtLeastOneFilterAppliedByTitle("color", filters) &&
                    !filteredColors.includes(variant.color?.internalTitle)
                  ) {
                    return false
                  }

                  return true
                }),
              showCompare: true,
              view: !isMobile && compareEnabled ? "toggle" : "normal",
              selected: isCompared(internalTitle),
              onCompare: (variant: BikeVariant) => {
                setComparedEnabled(true)
                setCompareBikes([
                  {
                    bike: getBike(internalTitle) || null,
                    variant,
                  },
                ])
                toast({
                  title: "Compare",
                  description: "Select up to three bikes to compare",
                  status: "info",
                  duration: 5000,
                  isClosable: true,
                  position: "top",
                  render: CompareToast,
                })
              },
              onClick: ({ variant }) => {
                if (!isMobile && compareEnabled) {
                  if (isCompared(internalTitle)) {
                    // Remove
                    setCompareBikes(
                      compareBikes.filter(
                        (bike) => bike.bike?.internalTitle !== internalTitle
                      )
                    )
                  } else {
                    // Add
                    setCompareBikes([
                      ...compareBikes.slice(0, 2),
                      { bike: getBike(internalTitle), variant },
                    ])
                  }
                }
              },
            } as BikeListLayoutProps["bikes"][0])
        )
        .filter((bike) => bike.variants?.length !== 0)
        .sort(sortMethods[sort])
    )
  }, [
    data,
    filters,
    inStockFilter,
    sort,
    bikeResults,
    bodyFit,
    compareEnabled,
    compareBikes,
    pricingMap,
  ])

  return (
    <>
      {pageContext && (
        <SEO
          title={data.collection?.seoTitle || pageContext.pageTitle}
          description={
            data.collection?.seoDescription?.seoDescription || undefined
          }
          location={location}
        />
      )}
      {data.collection?.banner?.fluid && (
        <Banner
          image={data.collection?.banner?.fluid as FluidObject}
          mobileImage={
            (data.collection?.mobileBanner?.fluid as FluidObject) || undefined
          }
          url={data.collection.bannerSlug}
        />
      )}
      <BikeListLayout
        mt="1rem"
        mb="2rem"
        bikes={listedBikes}
        enableBoxQuiz={data?.collection?.enableBoxQuiz}
        boxQuizImage={data?.collection?.boxQuizImage}
        boxQuizPosition={data?.collection?.boxQuizPosition}
        header={
          <>
            <MaxWidthGrid>
              <Flex
                gridColumn={["1/3", null, null, null, "2/14", "1/end"]}
                marginX={[null, null, null, null, null, "1.25rem"]}
                flexDir="column"
                mt={bp("1rem", "3rem")}
              >
                <Heading
                  as="h1"
                  size={isMobile ? "4" : "5"}
                  mt={["1.5625rem", null, null, null, "2.5rem"]}
                  mb="1.5625rem"
                  fontWeight="bold"
                  lineHeight="1.5rem"
                  color="night"
                  textAlign={["center", null, null, null, "left"]}
                  mx={[null, null, null, null, "0.875rem"]}
                >
                  {pageContext.pageTitle}
                </Heading>
                {/* Mobile ⌄ */}
                <LaptopDownOnly>
                  <MobileFilters filters={filters} setFilters={setFilters} />
                  <Flex justify="space-between" mb="3.75rem">
                    <InStockFilter
                      id="mobile-in-stock-filter"
                      enabled={!!inStockFilter}
                      setEnabled={setInStockFilter}
                    />
                    <SortDropdown sort={sort} setSort={setSort} />
                  </Flex>
                </LaptopDownOnly>
                {/* Laptop and Up Selectors ⌄ */}
                <LaptopUpOnly>
                  <Flex justify="space-between">
                    <DesktopFilters filters={filters} setFilters={setFilters}>
                      <InStockFilter
                        id="desktop-in-stock-filter"
                        enabled={!!inStockFilter}
                        setEnabled={setInStockFilter}
                      />
                    </DesktopFilters>
                    <SortDropdown sort={sort} setSort={setSort} />
                  </Flex>
                  <SelectedFilters filters={filters} setFilters={setFilters} />
                </LaptopUpOnly>
              </Flex>
            </MaxWidthGrid>
            <LaptopUpOnly>
              <Box width="100%" h="1px" bg="dividerLine" />
            </LaptopUpOnly>
          </>
        }
      />
      {data.collection?.seoContentBlock && (
        <SEOBlock
          label={`Learn more about ${pageContext.pageTitle}`}
          mt={bp("80", "200")}
          mb="80"
          image={data.collection?.seoBackgroundImage?.fluid as FluidObject}
          video={data.collection?.seoContentBlockVideo?.seoContentBlockVideo || ""}
          className="seo-content-block"
        >
          <Body as="h2" fontWeight="bold" mb="20">
            {pageContext.pageTitle}
          </Body>
          <ContentfulRichText richText={data.collection.seoContentBlock} />
        </SEOBlock>
      )}
      {/* Compare Bar */}
      {!isMobile && compareEnabled && (
        <CompareBar
          position="fixed"
          bottom="0"
          left="0"
          width={contentWidthResponsive}
          zIndex={1000000}
          bikes={compareBikes.map(({ bike, variant }) => {
            return {
              id: bike?.internalTitle || "",
              title: bike?.formattedTitle || "N/A",
              price: getFixedPrice(variant.pricing?.priceV2.amount),
              image: (variant.image || placeholderImage) as FluidObject,
            }
          })}
          onRemoveBike={(bike) =>
            setCompareBikes(
              compareBikes.filter(
                (compareBike) => compareBike.bike?.internalTitle !== bike?.id
              )
            )
          }
          onCompare={() => {
            compareModal.onOpen()
          }}
          onClose={() => setComparedEnabled(false)}
          isSidebarOpen={isSidebarOpen}
        />
      )}
      {/* Compare Drawer */}
      <Drawer
        placement="bottom"
        isOpen={!isMobile && compareModal.isOpen}
        onClose={compareModal.onClose}
      >
        <DrawerOverlay />
        <DrawerContent
          backgroundColor="white"
          borderRadius="1rem 1rem 0 0"
          zIndex={1000001}
        >
          <DrawerCloseButton pos="fixed" size="lg" top="2rem" right="2rem" />
          <MaxWidthGrid h="90vh" w="100%" gridColumnGap="0" overflowY="auto">
            <Box
              display={["none", null, null, null, null, "block"]}
              gridColumn="1/2"
              backgroundColor="backgroundOpaque"
            />
            <CompareTable
              gridColumn={["1/end", null, null, null, null, "2/14"]}
              bikes={compareBikes}
              onSpeedChange={(bikeIndex, newSpeed) => {
                const compareBike = compareBikes[bikeIndex]
                const listedBike = listedBikes.find(
                  (bike) => bike.id === compareBike.bike?.internalTitle
                )
                const variant = listedBike?.variants?.find(
                  (variant) => variant.speed === newSpeed
                )
                if (variant) {
                  const newCompareBikes = [...compareBikes]
                  newCompareBikes[bikeIndex] = {
                    bike: compareBike.bike,
                    variant,
                  }
                  setCompareBikes(newCompareBikes)
                }
              }}
            />
          </MaxWidthGrid>
        </DrawerContent>
      </Drawer>
    </>
  )
}

export default BicycleProductList
